import { getRouteApi } from '@tanstack/react-router'
import clsx from 'clsx'
import React, {
  useImperativeHandle,
  useState,
  type HTMLAttributes
} from 'react'
import { useTranslation } from 'react-i18next'

import SendMessageIcon from '@/assets/icons/send-message.svg?react'
import UnionIcon from '@/assets/icons/union.svg?react'
import Button from '@/components/Button/Button'
import Checkbox from '@/components/Checkbox/Checkbox'
import ConfirmModal from '@/components/ConfirmModal'
import Table, { type Column } from '@/components/Table/Table'
import TableTopBarActions from '@/components/TableTopBar/TableTopBarActions'
import SendAvailabilityRequestModal from '@/components/common/SendAvailabilityRequestModal/SendAvailabilityRequestModal'
import useDaysOfWeek from '@/hooks/useDaysOfWeek'
import { usePagination } from '@/hooks/usePagination'
import useRowSelection from '@/hooks/useRowSelection'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { toast } from '@/hooks/useToast'
import useSendBulkAvailabilityRequest from '@/mutations/useSendBulkAvailabilityRequest'
import useSemesters from '@/queries/useSemesters'
import useAuthStore from '@/store/useAuthStore'
import { getValue } from '@/utils/get-value'

import styles from './AvailabilityTable.module.scss'
import AvailabilityTableTabs from './AvailabilityTableTabs/AvailabilityTableTabs'
import useCopyAvailability from '../../mutations/useCopyAvailability'
import {
  useAvailabilitiesList,
  useAvailabilityUsersIds,
  useAvailabilityUsersCount,
  type AvailabilityList
} from '../../queries/useAvailabilityList'
import type { AvailabilityDay } from '../../types/availability-type'
import UserAvailabilityModal from '../UserAvailabilityModal/UserAvailabilityModal'

const routeApi = getRouteApi('/_auth/availability-management/')

type AvailabilityTableProps = {
  isArchivedSemester?: boolean
  columns: Column<AvailabilityList>[]
}

export type AvailabilityTableRef = {
  clearAllSelection: () => void
}

const AvailabilityTable = (
  props: AvailabilityTableProps,
  ref: React.ForwardedRef<AvailabilityTableRef>
) => {
  const { t } = useTranslation('availabilityManagement')
  const navigate = routeApi.useNavigate()
  const { page, pageSize, semester, day, name, role } = routeApi.useSearch()
  const { user, isSuperAdmin } = useAuthStore()

  const { data: semesters } = useSemesters()

  const currentSemester = semesters?.getSemester(semester)

  const [isCopyAvailabilityModalOpen, setIsCopyAvailabilityModalOpen] =
    useState(false)

  const [
    openSendBulkAvailabilityRequestModal,
    setOpenSendBulkAvailabilityRequestModal
  ] = useState(false)

  const [openAvailabilityEditModal, setOpenAvailabilityEditModal] =
    useState(false)

  const [selectedUserId, setSelectedUserId] = useState<string>()

  const daysOfWeek = useDaysOfWeek()

  const { isMobile } = useScreenResolution()

  const params = {
    semesterId: semester,
    userId: name?.map(getValue),
    role,
    page,
    pageSize
  }

  const {
    data: availability,
    isLoading: isAvailabilityLoading,
    refetch: fetchAvailabilityList
  } = useAvailabilitiesList(params)

  const { data: availabilityListIds, isLoading: isAvailabilityListIdsLoading } =
    useAvailabilityUsersIds(params)

  const { data: availabilityUsersCount } = useAvailabilityUsersCount(semester)

  const isLoading = isAvailabilityLoading || isAvailabilityListIdsLoading

  const count = availabilityListIds?.count || 0

  const rowSelection = useRowSelection({
    count,
    itemsToSelect: availabilityListIds?.ids || []
  })

  const handleOpenModal = (userId: string) => {
    setOpenAvailabilityEditModal(true)
    setSelectedUserId(userId)
  }

  const {
    mutate: sendAvailabilityRequest,
    isPending: isSendBulkAvailabilityRequestPending
  } = useSendBulkAvailabilityRequest({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('toast.successfully-sent-email')
      })
      setOpenSendBulkAvailabilityRequestModal(false)
      rowSelection.clearSelection()
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.failed-to-sent-email')
      })
    }
  })

  const { mutate: copyAvailablity, isPending: isLoadingCopyAvailability } =
    useCopyAvailability({
      onSuccess: () => {
        toast({
          variant: 'success',
          title: t('toast.successfully-copy-availability', {
            PREVIOUS_SEMESTER: semesters?.previousSemester.name
          })
        })
        setIsCopyAvailabilityModalOpen(false)
        rowSelection.clearSelection()
        fetchAvailabilityList()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: t('toast.failed-to-copy-availability', {
            PREVIOUS_SEMESTER: semesters?.previousSemester.name
          })
        })
      }
    })

  const handleCopyAvailability = () => {
    if (!semesters?.previousSemester || !currentSemester) return

    copyAvailablity({
      fromSemesterId: semesters.previousSemester.id,
      toSemesterId: currentSemester.id,
      usersIds: rowSelection.selectedItems
    })
  }

  const handleSendBulkAvailabilityRequest = (semesterId: string) => {
    sendAvailabilityRequest({
      semesterId,
      userIds: rowSelection.selectedItems
    })
  }

  const bulkActions = [
    {
      text: t('button.copy-availability-from'),
      icon: <UnionIcon />,
      onClick: () => setIsCopyAvailabilityModalOpen(true),
      hidden:
        currentSemester?.status === 'archived' || !semesters?.previousSemester
    },
    {
      text: t('button.send-availability-request'),
      icon: <SendMessageIcon />,
      onClick: () => setOpenSendBulkAvailabilityRequestModal(true)
    }
  ].filter(action => !action.hidden)

  const pagination = usePagination(routeApi)

  const handleTabChange = (value: AvailabilityDay) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        day: value
      })
    })
  }

  const canEditAvailability = isSuperAdmin && !props.isArchivedSemester

  const getUsernameByRowId = (rowId: string) =>
    availability?.list.find(item => item.id === rowId)?.username

  const getUsernameByUserId = (userId: string) =>
    availability?.list.find(item => item.userId === userId)?.username

  const tableComponents = {
    header: {
      table: (tableProps: HTMLAttributes<HTMLTableElement>) => (
        <table {...tableProps} className={styles.table} />
      ),
      wrapper: (headerProps: HTMLAttributes<HTMLTableSectionElement>) =>
        isMobile ? (
          <thead {...headerProps} className={styles.header}>
            <tr>
              <th className={styles.headerMobileWrapper}>
                <Checkbox
                  id="select-all"
                  variant="default"
                  label={t('label.select-all-users')}
                  hideLabel
                  checked={rowSelection.getSelectAllCheckboxValue()}
                  className={styles.checkbox}
                  value={!!rowSelection.selectedItems.length}
                  onChange={value => rowSelection.onSelectAll(value)}
                />
                <p className={styles.day}>
                  {daysOfWeek.find(({ value }) => value === day)?.labelLong}
                </p>
              </th>
            </tr>
          </thead>
        ) : (
          <thead {...headerProps} className={styles.header} />
        ),
      cell: (cellProps: HTMLAttributes<HTMLTableCellElement>) => (
        <th
          {...cellProps}
          className={clsx(cellProps.className, styles.headerCell)}
        />
      )
    },
    body: {
      row: (
        rowProps: HTMLAttributes<HTMLTableRowElement> & {
          'data-row-key': string
        }
      ) => (
        <tr
          {...rowProps}
          tabIndex={0}
          aria-label={t('label.open-user-availability-modal', {
            USER: getUsernameByRowId(rowProps['data-row-key'])
          })}
        />
      ),
      cell: (cellProps: HTMLAttributes<HTMLTableCellElement>) => (
        <td
          {...cellProps}
          className={clsx(
            cellProps.className,
            styles.cell,
            canEditAvailability && styles.clickable
          )}
        />
      )
    }
  }

  const selectedUserName = selectedUserId
    ? getUsernameByUserId(selectedUserId)
    : undefined

  useImperativeHandle(
    ref,
    () => ({
      clearAllSelection: () => rowSelection.clearSelection()
    }),
    [rowSelection]
  )

  return (
    <>
      {isMobile ? (
        <AvailabilityTableTabs
          value={day}
          onClick={handleTabChange}
          days={daysOfWeek.map(({ value, labelLong }) => ({
            title: labelLong,
            value
          }))}
        />
      ) : null}

      <Table
        id="availability-table"
        data={availability?.list || []}
        rowKey="userId"
        columns={props.columns}
        isLoading={isLoading}
        onRowClick={row =>
          canEditAvailability ? handleOpenModal(row.userId) : undefined
        }
        rowSelection={
          user?.isSuperAdmin
            ? {
                selectedItems: rowSelection.selectedItems,
                allCheckboxValue: rowSelection.getSelectAllCheckboxValue(),
                count,
                labelHeader: t('label.select-all-users'),
                labelItem: row =>
                  t('label.select-user', { USER: row.username }),
                onSelect: rowSelection.onSelect,
                onSelectAll: rowSelection.onSelectAll,
                checkboxVariant: 'default',
                disabled: props.isArchivedSemester
              }
            : undefined
        }
        count={count}
        totalCount={availabilityUsersCount}
        {...pagination}
        components={tableComponents}
        rightTopBar={
          user?.isSuperAdmin ? (
            <TableTopBarActions
              actions={bulkActions}
              selectedItemsLength={rowSelection.selectedItems.length}
              groupActionsOnMobile
            />
          ) : null
        }
        caption={t('header.availability')}
      />
      <SendAvailabilityRequestModal
        open={openSendBulkAvailabilityRequestModal}
        onOpenChange={setOpenSendBulkAvailabilityRequestModal}
        selectedSemester={semester}
        description={t('help.send-availability-request-email', {
          count: rowSelection.selectedItems.length
        })}
        onSendRequest={handleSendBulkAvailabilityRequest}
        sendRequestLoading={isSendBulkAvailabilityRequestPending}
      />
      {currentSemester ? (
        <UserAvailabilityModal
          title={t('header.availability-of-user-for-semester', {
            USER: selectedUserName,
            SEMESTER: currentSemester.name
          })}
          onOpenChange={value => {
            setOpenAvailabilityEditModal(value)
            if (!value) setSelectedUserId(undefined)
          }}
          open={openAvailabilityEditModal}
          semester={currentSemester}
          onSuccess={fetchAvailabilityList}
          userId={selectedUserId}
          userName={selectedUserName}
        />
      ) : null}

      <ConfirmModal
        open={isCopyAvailabilityModalOpen}
        onOpenChange={setIsCopyAvailabilityModalOpen}
        header={t('header.copy-availability-from', {
          SEMESTER: semesters?.previousSemester.name
        })}
        subheader={t('help.copy-availability', {
          count: rowSelection.selectedItems.length,
          CURRENT_SEMESTER: currentSemester?.name,
          PREVIOUS_SEMESTER: semesters?.previousSemester.name
        })}
        confirmButton={
          <Button
            onClick={handleCopyAvailability}
            loading={isLoadingCopyAvailability}
          >
            {t('button.copy-availability')}
          </Button>
        }
      />
    </>
  )
}

export default React.forwardRef<AvailabilityTableRef, AvailabilityTableProps>(
  AvailabilityTable
)
